<template>
    <Transition name="assistant-overlay" :duration="600">
        <div v-if="isActive" class="assistant-overlay" @click.self="closePanel">
            <section class="assistant">
                <header class="assistant__head">
                    <h3 class="assistant__title">
                        {{ $t('pages.assistant.title') }}
                    </h3>
                    <!-- The backend refuses a second client inside a bound
                         conversation and asks for a new one, so starting over
                         has to be one action rather than closing and
                         reopening. -->
                    <v-btn
                        v-if="hasConversation"
                        small
                        text
                        color="#967adc"
                        class="assistant__restart"
                        :disabled="isSending"
                        @click="startOver"
                    >
                        {{ $t('pages.assistant.newConversation') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn icon color="#55595c" @click="closePanel">
                        <v-icon>mdi-close-circle-outline</v-icon>
                    </v-btn>
                </header>

                <AssistantChatLog class="assistant__log" />

                <AssistantWigPreview />
                <AssistantClientCandidates />
                <AssistantPendingConfirm />
                <AssistantFailure />

                <!-- Never hidden, in any of the three states: a question can
                     arrive in all of them, including `open`. -->
                <AssistantComposer />
            </section>
        </div>
    </Transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import modalsMixin from '@/mixins/modals.mixin'
import { actionTypes as actionTypesConfirmer } from '@/store/modules/confirmer'
import popUpTypes from '@/types/pop-up-types'
import AssistantChatLog from './assistant-chat-log.vue'
import AssistantComposer from './assistant-composer.vue'
import AssistantWigPreview from './assistant-wig-preview.vue'
import AssistantClientCandidates from './assistant-client-candidates.vue'
import AssistantPendingConfirm from './assistant-pending-confirm.vue'
import AssistantFailure from './assistant-failure.vue'
import {
    assistantActionTypes,
    assistantGetterTypes,
} from '@/store/modules/assistant/types'

const { assistantPanel } = popUpTypes

export default {
    name: 'AssistantPanel',
    components: {
        AssistantChatLog,
        AssistantComposer,
        AssistantWigPreview,
        AssistantClientCandidates,
        AssistantPendingConfirm,
        AssistantFailure,
    },
    mixins: [modalsMixin],
    data() {
        return {
            selfName: assistantPanel,
        }
    },
    computed: {
        ...mapGetters('assistant', {
            hasConversation: assistantGetterTypes.GET_HAS_CONVERSATION,
            isSending: assistantGetterTypes.GET_IS_SENDING,
        }),
        // Present from a client card, absent from the wigs screen — the whole
        // difference between the two scenarios.
        clientId: function () {
            return this.extraData?.clientId
        },
    },
    watch: {
        isActive: function (isActive) {
            const html = document.querySelector('html')

            if (isActive) {
                this.openConversation({ clientId: this.clientId })
                html.classList.add('overflow-y-hidden-v2')
                return
            }

            html.classList.remove('overflow-y-hidden-v2')
        },
    },
    methods: {
        ...mapActions('assistant', {
            openConversation: assistantActionTypes.OPEN,
            startOver: assistantActionTypes.START_OVER,
        }),
        ...mapActions('confirmer', {
            confirmHandle: actionTypesConfirmer.confirmHandle,
        }),
        /**
         * Closing ends the conversation — reopening starts a fresh one — so an
         * accidental click on the backdrop should not throw away a dictation
         * halfway through. Nothing to lose on an untouched panel, so nothing is
         * asked there.
         */
        closePanel: async function () {
            if (!this.hasConversation) {
                this.closeHandle()
                return
            }

            try {
                await this.confirmHandle({
                    text: this.$t('pages.assistant.closeConfirm.text'),
                    yesText: this.$t('pages.assistant.closeConfirm.yes'),
                    noText: this.$t('pages.assistant.closeConfirm.no'),
                    // Ending the conversation is the action with a cost here,
                    // so it carries the warning colour and staying carries the
                    // safe one — the opposite of the defaults.
                    confirmColor: '#da4453',
                    cancelColor: '#3bafda',
                })
                this.closeHandle()
            } catch (error) {
                // Cancelled — the panel stays as it was.
            }
        },
    },
}
</script>

<style scoped lang="scss">
.assistant-overlay {
    position: fixed;
    // Deliberately below Vuetify's dialog layer (~202), so the close
    // confirmation opens over the panel rather than behind it. The tab strip is
    // hidden while this is open rather than outrun with a higher number.
    z-index: 200;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.28);
    will-change: opacity;
}

.assistant {
    position: absolute;
    top: 0;
    right: 0;
    // `dir="rtl"` mirrors the logical flow; an offset is physical and stays put.
    @include for-hebrew {
        right: auto;
        left: 0;
    }

    display: flex;
    flex-direction: column;
    width: 550px;
    max-width: 100vw;
    height: 100%;
    background: $color-primary-light;
    will-change: transform;

    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid #eceeef;
        flex-shrink: 0;
    }

    &__restart {
        text-transform: none;
        letter-spacing: normal;
        // Off the title: the two read as one phrase when they touch.
        margin-left: 10px;

        @include for-hebrew {
            margin-left: 0;
            margin-right: 10px;
        }
    }

    &__title {
        font-weight: 400;
        font-size: 18px;
        font-family: $font-muli;

        // Muli has no Hebrew glyphs; Varela Round covers the alphabet, the
        // final forms, niqqud and the geresh, and is already the app font.
        @include for-hebrew {
            font-family: $font-varela-round;
        }
    }

    // The only part that scrolls — the composer below has to stay reachable.
    &__log {
        flex-grow: 1;
        min-height: 0;
    }
}

.assistant-overlay-enter-active {
    transition: opacity 100ms;
}

.assistant-overlay-leave-active {
    transition: opacity 100ms 400ms;
}

.assistant-overlay-enter,
.assistant-overlay-leave-to {
    opacity: 0;
}

.assistant-overlay-enter-active .assistant {
    transition: transform 400ms 100ms cubic-bezier(0.36, 0.42, 0.44, 0.91);
}

.assistant-overlay-leave-active .assistant {
    transition: transform 400ms cubic-bezier(0.57, 0.38, 1, 0.63);
}

.assistant-overlay-enter .assistant,
.assistant-overlay-leave-to .assistant {
    transform: translateX(100%);

    @include for-hebrew {
        transform: translateX(-100%);
    }
}
</style>
